import dayjs from 'dayjs';
import { getParsedDateTime } from '@/util/helper/dateTime';
import { DATE_TIME_FORMAT_MAP } from '@/config/dateTime';
import { GENDER_OPTION_LIST, LANG_OPTION_LIST, NO_TUBE_OPTION, REQUIRED_GENDER_OPTION_LIST, } from '../../config/option';
import { PINNED_HOSPITAL_LIST } from '../../config/search';
import { DEFAULT_EXPECTED_START_TIME_HOUR, DEFAULT_EXPECTED_START_TIME_MINUTE, } from '../../config/time';
const getWithDisplayTitleOptionList = ({ optionList }) => optionList.map((option) => ({
    ...option,
    displayTitle: option.title,
}));
// gender
export const getGenderOptionList = () => GENDER_OPTION_LIST;
// hospital
export const getHospitalOptionList = ({ hospitalList, }) => {
    const pinnedHospitalList = PINNED_HOSPITAL_LIST.map((pinnedHospital) => {
        const hospitalData = hospitalList.find((hospital) => hospital.name === pinnedHospital.name && hospital.city === pinnedHospital.city);
        return hospitalData || null;
    }).filter((hospital) => hospital !== null);
    const filteredHospitalData = hospitalList.filter((hospital) => !PINNED_HOSPITAL_LIST.some((pinnedHospital) => pinnedHospital.name === hospital.name && pinnedHospital.city === hospital.city));
    const hospitalOptionList = pinnedHospitalList.concat(filteredHospitalData);
    return hospitalOptionList;
};
// hospitalBed
export const getHospitalBedOptionList = ({ hospitalBedList, }) => {
    if (!hospitalBedList)
        return [];
    const hospitalBedOptionList = getWithDisplayTitleOptionList({
        optionList: hospitalBedList.options,
    });
    return hospitalBedOptionList;
};
// symptom
export const getSymptomOptionList = ({ symptomList, }) => {
    if (!symptomList)
        return [];
    const symptomOptionList = getWithDisplayTitleOptionList({
        optionList: symptomList.options,
    });
    return symptomOptionList;
};
// tube
export const getTubeOptionList = ({ tubeList }) => {
    if (!tubeList)
        return [];
    const noTubeOption = {
        title: NO_TUBE_OPTION.title,
        wholeDayCharge: 0,
    };
    const withNoTubeOptionTubeList = [noTubeOption, ...tubeList.options];
    const tubeOptionList = getWithDisplayTitleOptionList({
        optionList: withNoTubeOptionTubeList,
    });
    return tubeOptionList;
};
// requiredGender
export const getRequiredGenderOptionList = () => REQUIRED_GENDER_OPTION_LIST;
// lang
export const getLangOptionList = () => LANG_OPTION_LIST;
// servedItem
export const getServedItemOptionList = ({ servedItemList, }) => {
    if (!servedItemList)
        return [];
    const servedItemOptionList = getWithDisplayTitleOptionList({
        optionList: servedItemList.options,
    });
    return servedItemOptionList;
};
// timeSlotList
const getDateWithDefaultHourAndMinute = ({ dateObj }) => {
    const dateWithDefaultHourAndMinute = dateObj
        .hour(DEFAULT_EXPECTED_START_TIME_HOUR)
        .minute(DEFAULT_EXPECTED_START_TIME_MINUTE)
        .startOf('minute'); // with 0 seconds and 0 milliseconds
    return dateWithDefaultHourAndMinute;
};
const getMinAllDayExpectedStartTimeObj = ({ ESTBufferHours }) => {
    const now = dayjs();
    const minStartTimeObj = now.add(ESTBufferHours, 'hour');
    const minuteUnit = 30;
    const minute = minStartTimeObj.minute();
    if (minute < minuteUnit) {
        // round to hh:30
        const roundedTime = minStartTimeObj.minute(minuteUnit).startOf('minute');
        return roundedTime;
    }
    // round to the next hour
    const roundedTime = minStartTimeObj.add(1, 'hour').minute(0).startOf('minute');
    return roundedTime;
};
export const getAllDayExpectedStartTimeDateOptionList = ({ ESTBufferHours, ESTFurthestDays, }) => {
    const minExpectedStartTimeObj = getMinAllDayExpectedStartTimeObj({
        ESTBufferHours,
    });
    const dateOptions = [];
    let currentDate = minExpectedStartTimeObj;
    while (dateOptions.length < ESTFurthestDays) {
        dateOptions.push(currentDate.toISOString());
        // reshape the minExpectedStartTimeObj after pushed it into the dateOptions
        // and before adding 1 day to it
        // so that the following dateOptions will have the reshaped time
        if (currentDate === minExpectedStartTimeObj) {
            const reshapedDate = getDateWithDefaultHourAndMinute({
                dateObj: currentDate,
            });
            currentDate = reshapedDate;
        }
        currentDate = currentDate.add(1, 'day');
    }
    return dateOptions;
};
export const getDefaultPartTimeExpectedStartTime = () => {
    const now = dayjs();
    const defaultPartTimeExpectedStartTime = getDateWithDefaultHourAndMinute({
        dateObj: now,
    }).toISOString();
    return defaultPartTimeExpectedStartTime;
};
const getMinPartTimeExpectedStartTimeObj = ({ partTimeExpectedStartTime, ESTBufferHours, }) => {
    const now = dayjs();
    const isAuxPartTimeExpectedStartTimeValid = dayjs(partTimeExpectedStartTime).isSameOrAfter(now.add(ESTBufferHours, 'hour'));
    const minPartTimeExpectedStartTimeObj = isAuxPartTimeExpectedStartTimeValid
        ? dayjs(partTimeExpectedStartTime)
        : dayjs(partTimeExpectedStartTime).add(1, 'day');
    return { minPartTimeExpectedStartTimeObj };
};
export const getPartTimeExpectedEndTime = ({ expectedStartTime, expectedServiceHours, }) => {
    if (!expectedStartTime || !expectedServiceHours)
        return '';
    const expectedEndTime = dayjs(expectedStartTime).add(expectedServiceHours, 'hour');
    return expectedEndTime.toISOString();
};
export const getPartTimeTimeSlotOptionList = ({ ESTBufferHours, ESTFurthestDays, partTimeExpectedStartTime, partTimeExpectedServiceHours, }) => {
    if (!partTimeExpectedStartTime ||
        !partTimeExpectedServiceHours ||
        !ESTBufferHours ||
        !ESTFurthestDays)
        return [];
    const { minPartTimeExpectedStartTimeObj } = getMinPartTimeExpectedStartTimeObj({
        partTimeExpectedStartTime,
        ESTBufferHours,
    });
    const optionList = [];
    let currentDate = minPartTimeExpectedStartTimeObj;
    while (optionList.length < ESTFurthestDays) {
        const currentExpectedStartTime = currentDate.toISOString();
        const currentExpectedEndTime = getPartTimeExpectedEndTime({
            expectedStartTime: currentExpectedStartTime,
            expectedServiceHours: partTimeExpectedServiceHours,
        });
        const { parsedDateTime: parsedCurrentExpectedStartTime } = getParsedDateTime({
            dateTime: currentExpectedStartTime,
            format: DATE_TIME_FORMAT_MAP.DATE,
        });
        const { parsedDateTime: parsedCurrentExpectedEndTime } = getParsedDateTime({
            dateTime: currentExpectedEndTime,
            format: DATE_TIME_FORMAT_MAP.TIME,
        });
        const isCrossDay = dayjs(currentExpectedEndTime).date() !== dayjs(currentExpectedStartTime).date();
        const option = {
            expectedStartTime: currentExpectedStartTime,
            expectedEndTime: currentExpectedEndTime,
            isCrossDay,
            parsedExpectedStartTime: parsedCurrentExpectedStartTime,
            parsedExpectedEndTime: isCrossDay
                ? `到隔天${parsedCurrentExpectedEndTime}`
                : parsedCurrentExpectedEndTime,
        };
        optionList.push(option);
        currentDate = currentDate.add(1, 'day');
    }
    return optionList;
};
